/**
 * MoveBlock — the dash-launched block (Brief 014 P1). Celeste's "Kevin": a SOLID that
 * shoots along the dash direction when the player dashes into it, travels until it hits a
 * wall (or `props.travel` tiles), then stops — reusable as a moving-cover puzzle piece.
 *
 * Self-poll trigger (the block has no side-hit spine hook): each idle step it checks for a
 * live dash whose body overlaps the block's expanded trigger box, and launches horizontally
 * along `player.dashDir`. Motion reuses the Brief 011 kinematic carry — `kinematicDelta`
 * moves a rider, and the block's own travel routes through `grid.collideX` so a wall stops
 * it. A short cooldown after stopping prevents an instant re-trigger.
 *
 * Determinism: dt·speed travel + grid collision + integer cooldown → ESLint-clean.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';

const DEFAULT_SPEED = 320;   // px/s while launched
const DEFAULT_TRAVEL = 4;    // tiles before it stops on its own
const RETRIGGER_COOLDOWN = 8;

export class MoveBlock implements Entity {
  readonly type = EntityType.MOVEBLOCK;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private speed: number;
  private maxTravel: number;
  private state: 'idle' | 'moving' = 'idle';
  private moveVx = 0;
  private traveled = 0;
  private cooldown = 0;
  private dx = 0;

  /** @param props `speed` (px/s; default 320), `travel` (tiles; default 4). */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    const sp = props?.speed;
    this.speed = typeof sp === 'number' && sp > 0 ? sp : DEFAULT_SPEED;
    const tr = props?.travel;
    this.maxTravel = (typeof tr === 'number' && tr > 0 ? tr : DEFAULT_TRAVEL) * ts;
  }

  solidBody(): AABB { return this.body; }

  update(dt: number, ctx: EntityContext): void {
    if (this.state === 'moving') {
      const before = this.body.x;
      const hit = ctx.grid.collideX(this.body, this.moveVx * dt);
      this.dx = this.body.x - before;
      this.traveled += Math.abs(this.dx);
      if (hit || this.traveled >= this.maxTravel) {
        this.state = 'idle';
        this.moveVx = 0;
        this.cooldown = RETRIGGER_COOLDOWN;
      }
    } else {
      this.dx = 0;
      if (this.cooldown > 0) { this.cooldown--; return; }
      const p = ctx.player;
      if (p.dashActiveSteps > 0 && this.dashOverlap(p.body)) {
        this.moveVx = p.dashDir * this.speed;
        this.state = 'moving';
        this.traveled = 0;
      }
    }
  }

  /** Player body overlaps the block expanded a few px (so a dash into its face counts). */
  private dashOverlap(pb: AABB): boolean {
    const m = 6;
    return pb.x < this.body.x + this.body.w + m && pb.x + pb.w > this.body.x - m
      && pb.y < this.body.y + this.body.h && pb.y + pb.h > this.body.y;
  }

  /** Per-step movement — carries a rider on top (Brief 011). */
  kinematicDelta(): { dx: number; dy: number } { return { dx: this.dx, dy: 0 }; }

  onPlayerOverlap(_ctx: EntityContext): void { /* solid — launched via the dash self-poll */ }

  spriteKey(): string { return 'moveblock'; }
  visible(): boolean { return true; }
}
